// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

// Translates the raw backend validation errors returned by
// POST /dashboard/ValidateManualInvoice into plain-language, operator-friendly
// guidance. The backend runs the shared France e-invoicing validators, whose
// error `code`s are a mix of stable string codes (EmptyPayload, SellerCompanyName…),
// opaque numeric SubErrorCodes (57 = seller tax id, 45 = buyer tax id, 11 = currency…),
// and a single reused batch code (VALIDATION_FAILED) whose meaning lives only in the
// message text. So we classify on the code AND the message, then map the resulting
// category to a friendly title + guidance.
//
// The raw code/message are ALWAYS preserved on the result so support can still see the
// exact validator output — for unrecognised errors the raw message is surfaced as the
// primary guidance so nothing is ever hidden.

/**
 * A backend validation error translated for display to a business operator.
 */
export interface FriendlyValidationError {
    /** Short, plain-language headline for the problem. */
    title: string;
    /** Actionable, plain-language explanation of what to fix. */
    guidance: string;
    /** True when the error matched a known category; false when it fell back to the raw message. */
    recognized: boolean;
    /** The raw error code from the backend (preserved for support/debugging). */
    technicalCode: string;
    /** The raw error message from the backend (preserved for support/debugging). */
    technicalMessage: string;
}

type FriendlyCategory =
    | 'parse'
    | 'country'
    | 'currency'
    | 'sellerIdentity'
    | 'buyerTaxId'
    | 'taxAmount'
    | 'creditNoteReference'
    | 'lineItems'
    | 'address'
    | 'documentType'
    | 'documentId'
    | 'documentDate'
    | 'sellerName';

const CATEGORY_TEXT: Record<FriendlyCategory, { title: string; guidance: string }> = {
    parse: {
        title: 'We couldn\u2019t read the invoice',
        guidance:
            'The form produced data the validation service could not parse. Refresh the tab and re-enter the invoice; if it keeps happening, contact the e-Invoicing team.',
    },
    country: {
        title: 'Country must be France',
        guidance:
            'This tool only supports French invoices. Make sure the country is France (FR) or a French territory.',
    },
    currency: {
        title: 'Currency must be euro (EUR)',
        guidance: 'France e-reporting only accepts EUR. Set the transaction currency to EUR.',
    },
    sellerIdentity: {
        title: 'Seller isn\u2019t a recognised French entity',
        guidance:
            'The seller company code is not registered as a French legal entity, or the seller SIREN/SIRET is malformed. Choose a valid French seller entity (SIREN = 9 digits, SIRET = 14 digits).',
    },
    buyerTaxId: {
        title: 'Check the buyer\u2019s tax ID',
        guidance:
            'The buyer\u2019s tax identifier is missing or malformed. Provide a valid SIREN (9 digits) or SIRET (14 digits); for a purchase report the buyer must be a known French entity.',
    },
    taxAmount: {
        title: 'Tax figures don\u2019t add up',
        guidance:
            'The tax figures are invalid \u2014 the tax amount may not match the tax rate, or the VAT rate may not be a permitted French rate. Check the VAT rate and the tax and base amounts on the document and its line items.',
    },
    creditNoteReference: {
        title: 'Missing original invoice reference',
        guidance:
            'A credit note must reference the original invoice\u2019s number (and date). Add the reference document details.',
    },
    lineItems: {
        title: 'Line item details are incomplete',
        guidance:
            'One or more line items are missing required information (for example a description or tax details). Complete every line item.',
    },
    address: {
        title: 'Address is missing or invalid',
        guidance: 'Check the seller and buyer addresses \u2014 one of them is missing or invalid.',
    },
    documentType: {
        title: 'Unsupported document type',
        guidance: 'Only invoices, credit notes and payment receipts are supported for France.',
    },
    documentId: {
        title: 'Missing invoice number',
        guidance: 'Enter a document / invoice number before creating the payload.',
    },
    documentDate: {
        title: 'Missing or invalid invoice date',
        guidance: 'Enter a valid invoice (document) date.',
    },
    sellerName: {
        title: 'Missing seller name',
        guidance: 'Enter the seller (declarant) company name.',
    },
};

interface CategoryRule {
    category: FriendlyCategory;
    /** @param code lower-cased backend code. @param message lower-cased backend message. */
    test: (code: string, message: string) => boolean;
}

// Ordered most-specific -> least-specific. First match wins.
// Predicates are deliberately narrow (compound keyword checks) so wording changes in
// one validator message are unlikely to be misclassified into an unrelated category,
// and so the reused VALIDATION_FAILED code is disambiguated purely from its message.
const RULES: CategoryRule[] = [
    {
        category: 'parse',
        test: (code) => code === 'emptypayload' || code === 'deserializationerror',
    },
    {
        category: 'country',
        test: (code, message) =>
            code === '10' ||
            ((message.includes('country code') || message.includes('countrycode')) &&
                (message.includes('fr') || message.includes('french') || message.includes('france'))),
    },
    {
        category: 'currency',
        test: (code, message) =>
            code === '11' || (message.includes('currency') && message.includes('eur')),
    },
    {
        category: 'sellerIdentity',
        test: (code, message) =>
            code === '57' ||
            ((message.includes('company code') || message.includes('companycode')) &&
                message.includes('registered in france legal entities')) ||
            (message.includes('seller') &&
                (message.includes('siren') || message.includes('siret') || message.includes('tax id'))),
    },
    {
        category: 'buyerTaxId',
        test: (code, message) =>
            code === '45' ||
            message.includes('buyer tax id') ||
            (message.includes('buyer') &&
                (message.includes('siren') || message.includes('siret') || message.includes('vat'))),
    },
    {
        category: 'taxAmount',
        test: (code, message) =>
            code === '16' ||
            message.includes('tax amount') ||
            message.includes('vat rate') ||
            message.includes('taxpercentage') ||
            (message.includes('tax rate') && message.includes('not valid')),
    },
    {
        category: 'creditNoteReference',
        test: (code, message) =>
            code === '59' ||
            message.includes('referencedocumentinfo') ||
            (message.includes('credit note') && message.includes('reference')),
    },
    {
        category: 'lineItems',
        test: (_code, message) => message.includes('line item') || message.includes('lineitem'),
    },
    {
        category: 'address',
        test: (code, message) =>
            code === '41' ||
            code === '43' ||
            (message.includes('address') &&
                (message.includes('seller') || message.includes('customer') || message.includes('soldto'))),
    },
    {
        category: 'documentType',
        test: (code, message) =>
            code === '1' ||
            (message.includes('document type') &&
                (message.includes('not supported') || message.includes('not valid'))),
    },
    {
        category: 'documentId',
        test: (code, message) =>
            code === 'documentid' ||
            code === '52' ||
            (message.includes('document id') && message.includes('required')) ||
            message.includes('documentid is required'),
    },
    {
        category: 'documentDate',
        test: (code, message) =>
            code === 'documentcreateddateinutc' ||
            code === '2' ||
            message.includes('documentdate is required') ||
            (message.includes('document date') &&
                (message.includes('required') || message.includes('valid date'))),
    },
    {
        category: 'sellerName',
        test: (code, message) =>
            code === 'sellercompanyname' ||
            ((message.includes('company name') || message.includes('companyname')) &&
                (message.includes('seller') || message.includes('declarant'))),
    },
];

/**
 * Translates a raw backend validation error into operator-friendly guidance.
 * Recognised errors get a friendly title + actionable guidance while still exposing the
 * raw code/message; unrecognised errors surface the original backend message as the
 * guidance so the operator always sees an accurate diagnostic.
 *
 * @param code The raw error code from the backend.
 * @param message The raw error message from the backend.
 */
export function toFriendlyError(code: string, message: string): FriendlyValidationError {
    const safeCode = code ?? '';
    const safeMessage = message ?? '';
    const codeLower = safeCode.trim().toLowerCase();
    const messageLower = safeMessage.toLowerCase();

    const rule = RULES.find((r) => r.test(codeLower, messageLower));

    if (rule) {
        const text = CATEGORY_TEXT[rule.category];
        return {
            title: text.title,
            guidance: text.guidance,
            recognized: true,
            technicalCode: safeCode,
            technicalMessage: safeMessage,
        };
    }

    return {
        title: 'Validation issue',
        guidance: safeMessage || 'The validation service reported an issue with the invoice.',
        recognized: false,
        technicalCode: safeCode,
        technicalMessage: safeMessage,
    };
}
